/**
 * CareTeamPage
 * Main care team overview with grid/list views
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Users, Loader2, Search, Phone, Mail } from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import { useCareTeamPage, useCareTeamTypes } from '@/features/care-team/hooks/useCareTeam';
import { useCareTeamGoalsStore } from '@/features/care-team/stores/careTeamStore';
import type { CareTeamMember } from '@/features/care-team/types/careTeamTypes';

export function CareTeamPage() {
  const navigate = useNavigate();
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProviderSearch, setShowProviderSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { viewMode, setViewMode } = useCareTeamGoalsStore();
  const { data: memberTypes } = useCareTeamTypes();

  // Only call useCareTeamPage when we have a familyId
  const {
    careTeam,
    total,
    isLoading,
    error,
    createMember,
    isCreating
  } = useCareTeamPage({ familyId: familyId || '' });

  const handleMemberClick = (member: CareTeamMember) => {
    navigate(`/family/care-team/${member.id}`);
  };

  const handleExport = async () => {
    console.log('Export care team');
  };

  // Show loading state while family context is loading
  if (isFamilyLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sage animate-spin mx-auto mb-4" />
          <p className="text-warm-gray">Loading...</p>
        </div>
      </div>
    );
  }

  // Show no family state if user doesn't have a family
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading care team. Please try again.</p>
      </div>
    );
  }

  // Filter care team by search
  const filteredCareTeam = (careTeam || []).filter((member: CareTeamMember) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.name?.toLowerCase().includes(query) ||
      member.specialty?.toLowerCase().includes(query) ||
      member.organization?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-navy">Care Team</h1>
          <p className="text-warm-gray mt-1">
            {total} provider{total !== 1 ? 's' : ''} on your team
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => setShowProviderSearch(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-sage text-sage hover:bg-sage/10 rounded-lg transition-colors"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Find Provider</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white hover:bg-sage-dark rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Member</span>
          </button>
        </div>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <input
            type="text"
            placeholder="Search care team..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        <div className="flex items-center gap-2">
          {(['grid', 'list', 'category'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                viewMode === mode
                  ? 'bg-sage text-white'
                  : 'text-navy hover:bg-cream'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Care Team Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-cream rounded-full" />
                <div className="flex-1">
                  <div className="h-5 bg-cream rounded w-3/4 mb-2" />
                  <div className="h-4 bg-cream rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredCareTeam.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-sage" />
          </div>
          <h3 className="font-display text-xl font-semibold text-navy mb-2">
            {searchQuery ? 'No matching providers' : 'No care team members yet'}
          </h3>
          <p className="text-warm-gray mb-6 max-w-md mx-auto">
            {searchQuery
              ? 'Try adjusting your search terms.'
              : 'Add doctors, therapists, and other care providers to build your care team.'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Your First Provider
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCareTeam.map((member: CareTeamMember) => (
            <CareTeamCard
              key={member.id}
              member={member}
              onClick={() => handleMemberClick(member)}
            />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {filteredCareTeam.map((member: CareTeamMember) => (
            <CareTeamListItem
              key={member.id}
              member={member}
              onClick={() => handleMemberClick(member)}
            />
          ))}
        </div>
      ) : (
        <CareTeamByCategory
          members={filteredCareTeam}
          onMemberClick={handleMemberClick}
        />
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <AddCareTeamMemberModal
          familyId={familyId}
          memberTypes={memberTypes || []}
          onClose={() => setShowAddModal(false)}
          onSubmit={async (data) => {
            await createMember(data);
            setShowAddModal(false);
          }}
          isSubmitting={isCreating}
        />
      )}

      {/* Provider Search Modal */}
      {showProviderSearch && (
        <ProviderSearchModal
          onClose={() => setShowProviderSearch(false)}
          onSelect={() => {
            setShowProviderSearch(false);
            setShowAddModal(true);
          }}
        />
      )}
    </div>
  );
}

// Care Team Card Component
function CareTeamCard({
  member,
  onClick
}: {
  member: CareTeamMember;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl p-6 text-left hover:shadow-lg transition-all hover:-translate-y-0.5 border border-warm-gray/10"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-sage/10 to-sage/20 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-xl font-semibold text-sage">
            {member.name?.charAt(0) || '?'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-navy truncate">{member.name}</h3>
          <p className="text-sm text-sage">{member.specialty || member.memberType?.name}</p>
          {member.organization && (
            <p className="text-sm text-warm-gray truncate">{member.organization}</p>
          )}
        </div>
        {member.isPrimary && (
          <span className="px-2 py-1 bg-sage/10 text-sage text-xs font-medium rounded-full">
            Primary
          </span>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-warm-gray/10 space-y-2">
        {member.phone && (
          <div className="flex items-center gap-2 text-sm text-warm-gray">
            <Phone className="w-4 h-4" />
            <span>{member.phone}</span>
          </div>
        )}
        {member.email && (
          <div className="flex items-center gap-2 text-sm text-warm-gray">
            <Mail className="w-4 h-4" />
            <span className="truncate">{member.email}</span>
          </div>
        )}
      </div>
    </button>
  );
}

// Care Team List Item Component
function CareTeamListItem({
  member,
  onClick
}: {
  member: CareTeamMember;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-lg p-4 text-left hover:shadow-md transition-all flex items-center gap-4 border border-warm-gray/10"
    >
      <div className="w-12 h-12 bg-gradient-to-br from-sage/10 to-sage/20 rounded-full flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-semibold text-sage">
          {member.name?.charAt(0) || '?'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-navy">{member.name}</h3>
          {member.isPrimary && (
            <span className="px-2 py-0.5 bg-sage/10 text-sage text-xs font-medium rounded-full">
              Primary
            </span>
          )}
        </div>
        <p className="text-sm text-sage">{member.specialty || member.memberType?.name}</p>
      </div>
      <div className="hidden sm:flex items-center gap-4 text-sm text-warm-gray">
        {member.phone && (
          <span className="flex items-center gap-1">
            <Phone className="w-4 h-4" />
            {member.phone}
          </span>
        )}
      </div>
    </button>
  );
}

// Care Team By Category Component
function CareTeamByCategory({
  members,
  onMemberClick
}: {
  members: CareTeamMember[];
  onMemberClick: (member: CareTeamMember) => void;
}) {
  const categories = members.reduce((acc, member) => {
    const category = member.memberType?.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(member);
    return acc;
  }, {} as Record<string, CareTeamMember[]>);

  const categoryLabels: Record<string, string> = {
    medical: 'Medical Providers',
    therapy: 'Therapists & Counselors',
    education: 'Education & School',
    support: 'Support Services',
    Other: 'Other'
  };

  return (
    <div className="space-y-8">
      {Object.entries(categories).map(([category, categoryMembers]) => (
        <div key={category}>
          <h3 className="font-display text-lg font-semibold text-navy mb-4">
            {categoryLabels[category] || category}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryMembers.map((member) => (
              <CareTeamCard
                key={member.id}
                member={member}
                onClick={() => onMemberClick(member)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Simple Add Member Modal
function AddCareTeamMemberModal({
  familyId,
  memberTypes,
  onClose,
  onSubmit,
  isSubmitting
}: {
  familyId: string;
  memberTypes: any[];
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    organization: '',
    phone: '',
    email: '',
    memberTypeId: ''
  });

  return (
    <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lifted w-full max-w-md">
        <div className="p-6 border-b border-warm-gray/10">
          <h2 className="text-xl font-display font-semibold text-navy">Add Care Team Member</h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(formData);
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-navy mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy mb-1">Specialty</label>
            <input
              type="text"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy mb-1">Organization</label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-warm-gray/20 text-navy rounded-lg hover:bg-cream transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name}
              className="flex-1 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Simple Provider Search Modal
function ProviderSearchModal({
  onClose,
  onSelect
}: {
  onClose: () => void;
  onSelect: (provider: any) => void;
}) {
  return (
    <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lifted w-full max-w-lg p-6">
        <h2 className="text-xl font-display font-semibold text-navy mb-4">Find Provider</h2>
        <p className="text-warm-gray mb-4">
          Provider directory search coming soon. For now, add providers manually.
        </p>
        <button
          onClick={onClose}
          className="w-full py-2 border border-warm-gray/20 text-navy rounded-lg hover:bg-cream transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default CareTeamPage;
